export type ResumeTemplateKey = "software" | "frontend" | "backend" | "devops" | "designer" | "support" | "entry";

export interface ResumeSnippet {
  label: string;
  description: string;
  markdown: string;
}

export const resumeSnippets: ResumeSnippet[] = [
  {
    label: "Experience",
    description: "Add a work experience entry",
    markdown: `### Job Title — Company
Jan 2022 – Present

- Built...
- Improved...
- Led...
`,
  },
  {
    label: "Project",
    description: "Add a project entry",
    markdown: `### Project Name

- Built with...
- Solved...
`,
  },
  {
    label: "Education",
    description: "Add an education or certification entry",
    markdown: `Degree or Certification — Institution
`,
  },
  {
    label: "Skills",
    description: "Add a skills list",
    markdown: `Skill One, Skill Two, Skill Three`,
  },
  {
    label: "Certification",
    description: "Add a certification entry",
    markdown: `### Certification Name — Issuing Organization
2024
`,
  },
  {
    label: "Summary",
    description: "Add a professional summary section",
    markdown: `## Summary

Your role focused on... with experience in...
`,
  },
];

export interface ResumeTemplate {
  key: ResumeTemplateKey;
  name: string;
  markdown: string;
}

export interface ResumeChecklistItem {
  label: string;
  passed: boolean;
  help: string;
}

export interface ResumeStrengthResult {
  score: number;
  items: ResumeChecklistItem[];
}

const starterTemplates: ResumeTemplate[] = [
  {
    key: "software",
    name: "Software Engineer",
    markdown: `# Your Name

City, Country · email@example.com · github.com/yourname · linkedin.com/in/yourname · Remote

## Summary

Software engineer focused on building reliable products for distributed teams.

## Skills

Go, TypeScript, SQL, Docker, AWS, REST APIs

## Experience

### Software Engineer — Company Name
Jan 2022 – Present

- Built a production feature that improved activation by 25%.
- Led reliability improvements that reduced incidents by 30%.
- Collaborated across product, design, and engineering in a remote team.

## Projects

### Project Name

- Built with Go, TypeScript, and PostgreSQL.
- Solved a clear user problem with a simple, maintainable design.

## Education

Degree or Certification — Institution
`,
  },
  {
    key: "frontend",
    name: "Frontend Developer",
    markdown: `# Your Name

City, Country · email@example.com · github.com/yourname · portfolio.dev · Remote

## Summary

Frontend developer creating accessible, fast, and polished user experiences.

## Skills

TypeScript, Alpine.js, Tailwind CSS, HTML, CSS, Testing, Accessibility

## Experience

### Frontend Developer — Company Name
Jan 2022 – Present

- Improved page performance by 35% through focused frontend optimization.
- Built reusable UI patterns used across 6 product surfaces.
- Partnered with designers and backend engineers in a remote team.

## Projects

### Project Name

- Built a responsive interface with TypeScript and Tailwind CSS.
- Added tests for core user flows.
`,
  },
  {
    key: "backend",
    name: "Backend Developer",
    markdown: `# Your Name

City, Country · email@example.com · github.com/yourname · linkedin.com/in/yourname · Remote

## Summary

Backend developer focused on reliable APIs, clean data models, and production systems.

## Skills

Go, SQL, PostgreSQL, Docker, AWS, REST APIs, Observability

## Experience

### Backend Developer — Company Name
Jan 2022 – Present

- Built an API that improved application completion by 32%.
- Reduced background job failures by 40% with better retries and logging.
- Worked with distributed teams across product, support, and engineering.

## Projects

### Project Name

- Designed a backend service with Go and PostgreSQL.
- Added observability and tests for critical workflows.
`,
  },
  {
    key: "devops",
    name: "DevOps / Cloud Engineer",
    markdown: `# Your Name

City, Country · email@example.com · github.com/yourname · Remote

## Summary

Cloud engineer focused on secure, observable, and cost-aware infrastructure.

## Skills

AWS, Terraform, Docker, CI/CD, Linux, Monitoring, Incident Response

## Experience

### DevOps Engineer — Company Name
Jan 2022 – Present

- Reduced deployment time by 45% by improving CI/CD pipelines.
- Managed cloud infrastructure with Terraform across production environments.
- Improved alert quality and reduced noisy pages by 30%.
`,
  },
  {
    key: "designer",
    name: "Product Designer",
    markdown: `# Your Name

City, Country · email@example.com · portfolio.dev · linkedin.com/in/yourname · Remote

## Summary

Product designer focused on clear, usable experiences for distributed teams.

## Skills

Product Design, UX Research, Figma, Prototyping, Design Systems, Accessibility

## Experience

### Product Designer — Company Name
Jan 2022 – Present

- Redesigned onboarding and improved activation by 18%.
- Built design system components used by 5 product teams.
- Ran remote research sessions with users across 4 time zones.
`,
  },
  {
    key: "support",
    name: "Customer Support / Remote Ops",
    markdown: `# Your Name

City, Country · email@example.com · linkedin.com/in/yourname · Remote

## Summary

Customer support specialist focused on clear communication and fast issue resolution.

## Skills

Customer Support, Documentation, Zendesk, Troubleshooting, Remote Operations

## Experience

### Customer Support Specialist — Company Name
Jan 2022 – Present

- Improved first-response time by 28% through workflow improvements.
- Wrote help center articles that reduced repeat tickets by 20%.
- Supported customers asynchronously across multiple time zones.
`,
  },
  {
    key: "entry",
    name: "Entry-level / Career Switcher",
    markdown: `# Your Name

City, Country · email@example.com · github.com/yourname · Remote

## Summary

Motivated candidate building practical skills through projects, learning, and hands-on work.

## Skills

TypeScript, Go, SQL, Git, Testing, Communication

## Projects

### Project Name

- Built a working application that solves a clear problem.
- Practiced clean code, testing, and deployment basics.

## Experience

### Previous Role — Company Name
Jan 2020 – Present

- Improved a workflow by 20% through documentation and automation.
- Collaborated with teammates in a remote or hybrid environment.

## Education

Course, Degree, or Certification — Institution
`,
  },
];

export function getStarterTemplates(): ResumeTemplate[] {
  return starterTemplates;
}

export function getStarterTemplate(key: ResumeTemplateKey): ResumeTemplate {
  return starterTemplates.find((template) => template.key === key) ?? starterTemplates[0];
}

export interface InsertSnippetResult {
  text: string;
  cursorPos: number;
}

export function insertSnippet(currentText: string, snippet: string, cursorStart: number, cursorEnd: number): InsertSnippetResult {
  const before = currentText.slice(0, cursorStart);
  const after = currentText.slice(cursorEnd);
  const text = before + snippet + after;
  const cursorPos = (before + snippet).length;
  return { text, cursorPos };
}

export function markdownToHtml(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const html: string[] = [];
  let listOpen = false;

  const closeList = () => {
    if (listOpen) {
      html.push("</ul>");
      listOpen = false;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (line === "") {
      closeList();
      continue;
    }

    if (line.startsWith("### ")) {
      closeList();
      html.push(`<h3>${formatInline(line.slice(4))}</h3>`);
      continue;
    }

    if (line.startsWith("## ")) {
      closeList();
      html.push(`<h2>${formatInline(line.slice(3))}</h2>`);
      continue;
    }

    if (line.startsWith("# ")) {
      closeList();
      html.push(`<h1>${formatInline(line.slice(2))}</h1>`);
      continue;
    }

    if (line.startsWith("- ")) {
      if (!listOpen) {
        html.push("<ul>");
        listOpen = true;
      }
      html.push(`<li>${formatInline(line.slice(2))}</li>`);
      continue;
    }

    closeList();
    html.push(`<p>${formatInline(line)}</p>`);
  }

  closeList();
  return html.join("\n");
}

export function evaluateResumeStrength(markdown: string): ResumeStrengthResult {
  const plainText = markdown.toLowerCase();
  const wordCount = markdown.trim().split(/\s+/).filter(Boolean).length;
  const contactLine = markdown.split("\n").find((line) => /[\w.-]+@[\w.-]+\.[a-z]{2,}/i.test(line)) ?? "";
  const hasEmail = /[\w.-]+@[\w.-]+\.[a-z]{2,}/i.test(markdown);
  const hasLocationOrRemote = /\b(remote|distributed)\b/i.test(contactLine) || /[a-z][a-z .-]+,\s*[a-z][a-z .-]+/i.test(contactLine);
  const hasRelevantLink = /github\.com|linkedin\.com|https?:\/\//i.test(markdown);
  const hasProjectOrPortfolio = /^##\s+(projects|portfolio|selected projects)/im.test(markdown) || /portfolio|behance\.net|dribbble\.com|github\.io/i.test(markdown);
  const measurableBullets = markdown.match(/^\s*-\s+.*(\d+%|\$\d+|\d+x|\d+\s+(users|services|teams|hours|days|minutes|customers|projects))/gim) ?? [];
  const items: ResumeChecklistItem[] = [
    {
      label: "Contact info",
      passed: hasEmail && hasLocationOrRemote,
      help: "Recruiters need a clear email and location or remote availability.",
    },
    {
      label: "Professional summary",
      passed: /^##\s+(summary|profile|professional summary)/im.test(markdown),
      help: "Add a brief profile that states your role, strengths, and target work.",
    },
    {
      label: "Skills section",
      passed: /^##\s+(skills|technical skills|core skills)/im.test(markdown),
      help: "List searchable tools, languages, platforms, and domain skills.",
    },
    {
      label: "Work experience",
      passed: /^##\s+(experience|work experience|professional experience|employment)/im.test(markdown),
      help: "Show recent roles with company names, dates, and achievement bullets.",
    },
    {
      label: "Projects or portfolio",
      passed: hasProjectOrPortfolio,
      help: "Include project work or a portfolio/GitHub link recruiters can inspect.",
    },
    {
      label: "Education or certifications",
      passed: /^##\s+(education|certifications|certificates|credentials)/im.test(markdown),
      help: "Add degrees, bootcamps, courses, or certifications when relevant.",
    },
    {
      label: "Relevant links",
      passed: hasRelevantLink,
      help: "Include LinkedIn, GitHub, portfolio, or other role-relevant links.",
    },
    {
      label: "Measurable impact",
      passed: measurableBullets.length >= 1,
      help: "Use numbers for outcomes: percentages, users, revenue, time saved, or scale.",
    },
    {
      label: "Remote-work signal",
      passed: /remote|distributed|async|time zone|timezone/.test(plainText),
      help: "Mention remote collaboration, async work, or time zone experience.",
    },
    {
      label: "ATS-friendly length",
      passed: wordCount > 120 && wordCount <= 850,
      help: "Aim for enough detail to prove fit without drifting past 1–2 pages.",
    },
  ];

  const passed = items.filter((item) => item.passed).length;
  return {
    score: Math.round((passed / items.length) * 100),
    items,
  };
}

export default function resumeBuilder() {
  const initialTemplate = getStarterTemplate("software");

  return {
    templates: getStarterTemplates(),
    selectedTemplate: initialTemplate.key,
    markdown: initialTemplate.markdown,
    atsMode: false,
    strengthExpanded: true,
    snippets: resumeSnippets,

    insertSnippet(snippetMarkdown: string) {
      const textarea = this.$refs.editor as HTMLTextAreaElement | undefined;
      if (!textarea) {
        this.markdown += "\n\n" + snippetMarkdown;
        return;
      }

      const result = insertSnippet(
        textarea.value,
        snippetMarkdown,
        textarea.selectionStart,
        textarea.selectionEnd,
      );

      this.markdown = result.text;

      this.$nextTick(() => {
        textarea.selectionStart = result.cursorPos;
        textarea.selectionEnd = result.cursorPos;
        textarea.focus();
      });
    },

    get previewHtml() {
      return markdownToHtml(this.markdown);
    },

    get strength() {
      return evaluateResumeStrength(this.markdown);
    },

    useTemplate(key: ResumeTemplateKey) {
      const template = getStarterTemplate(key);
      this.selectedTemplate = template.key;
      this.markdown = template.markdown;
    },

    toggleStrength() {
      this.strengthExpanded = !this.strengthExpanded;
    },

    downloadPdf() {
      document.body.classList.add("resume-printing");
      window.print();
      window.setTimeout(() => document.body.classList.remove("resume-printing"), 500);
    },
  };
}

function formatInline(value: string): string {
  return escapeHtml(value)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
